// src/hooks/useUsers.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listUsers,
  getUserDetail,
  createUser,
  updateUser,
  setUserActive,
  setUserPassword,
  type ListUsersParams,
  type UserUpsertIn,
} from "@/services/users";

export const USERS_QK = (params: ListUsersParams) => ["users", params] as const;

export function useUsers(params: ListUsersParams) {
  return useQuery({
    queryKey: USERS_QK(params),
    queryFn: () => listUsers(params),
    staleTime: 20_000,
  });
}

export function useUserDetail(userId: string | undefined) {
  return useQuery({
    queryKey: ["user_detail", userId],
    queryFn: () => getUserDetail(userId as string),
    enabled: !!userId,
    staleTime: 20_000,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: UserUpsertIn) => createUser(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UserUpsertIn }) =>
      updateUser(id, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["user_detail", variables.id] });
    },
  });
}

export function useSetUserActive() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      setUserActive(id, is_active),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["user_detail", variables.id] });
    },
  });
}

export function useSetUserPassword() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      setUserPassword(id, password),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["user_detail", variables.id] });
    },
  });
}
